import {DataPointColumnsToDataType, DataType, DataTypeToTSType} from 'data-frames/DataFrameTypes';

// Define coerce values as null if a row should simply be filtered if a value is null in a column of the given type
const DATA_TYPE_TO_COERCE_VALUE: {[key in DataType]: DataTypeToTSType[key] | null} = {
  [DataType.BOOLEAN]: false,
  [DataType.DATE_OR_TIMESTAMP]: null,
  [DataType.JSON]: null,
  [DataType.NUMBER]: null,
  [DataType.STRING]: 'No value',
};

/**
 * Depending on the DataType of the column, coerce null values or filter rows containing null values
 *  - Date: Filter
 *  - Number: Filter
 *  - String: Coerce to "No value"
 *
 * Example usage:
 *
 *    coerceOrFilterNulls(
 *      [
 *         {number: null, date: '2020-01-01', string: 'hello'},
 *         {number: 1, date: null, string: 'hello'},
 *         {number: 1, date: '2020-01-01', string: null},
 *      ],
 *      {
 *         number: DataType.NUMBER,
 *         date: DataType.DATE_OR_TIMESTAMP
 *         string: DataType.STRING
 *      }
 *    )
 *
 * Output:
 *
 *    [
 *      {number: 1, date: '2020-01-01', string: "No value"}
 *    ]
 *
 * @template DataPoint The type of a row in the dataset, should be a map where each key is the column name.
 * @param dataset Array of rows that are of type DataPoint
 * @param columnTypes Map of every columnName to its type
 * @param columnsToFilterNulls Array of columns for which to filter null values
 *
 * @deprecated This function is implemented with an old version of the DataFrame interface. New uses are discouraged,
 * instead the function should be converted to use the new interface.
 */
// TODO(BNCH-24546): Remove or reimplement data frame utils that use old data frame formats
export default function coerceOrFilterNulls<DataPoint>(
  dataset: Array<DataPoint>,
  columnTypes: DataPointColumnsToDataType<DataPoint>,
  columnsToFilterNulls: Array<keyof DataPoint>
): Array<DataPoint> {
  const columnNames = Object.keys(columnTypes) as Array<keyof DataPoint>;
  return dataset
    .map((row) => coerceRow(row, columnNames, columnTypes))
    .filter((row) => !shouldFilterRow(row, columnsToFilterNulls));
}

export function coerceRow<DataPoint>(
  row: DataPoint,
  columnNames: Array<keyof DataPoint>,
  columnTypes: DataPointColumnsToDataType<DataPoint>
): DataPoint {
  return columnNames.reduce((accumulator, columnName) => {
    const columnType: DataType = columnTypes[columnName];
    const coercedValue = row[columnName] ?? DATA_TYPE_TO_COERCE_VALUE[columnType];

    return {
      ...accumulator,
      [columnName]: coercedValue,
    };
  }, {}) as DataPoint;
}

// Assumes that coerceRow was run first, so it doesn't check that the coercion strategy is FILTER
export function shouldFilterRow<DataPoint>(row: DataPoint, columnNames: Array<keyof DataPoint>): boolean {
  return columnNames.some((column) => row[column] == null);
}
